import { JWTPayload } from './schemas';
import { tenantHeaders as tenantHeadersImpl, withTenant, getTenant } from './tenant';

type StoredAuth = { token?: string; tenant?: string } | null;

export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    return JWTPayload.parse(payload);
  } catch {
    return null;
  }
}

export function isTokenExpired(payload: JWTPayload): boolean {
  return Date.now() / 1000 > payload.exp;
}

export function isTokenValid(token: string): boolean {
  const payload = decodeJWT(token);
  if (!payload) return false;
  return !isTokenExpired(payload);
}

export function getTenantFromToken(token: string): string | null {
  const payload = decodeJWT(token);
  return payload?.tenantId || payload?.tenant || payload?.tid || null;
}

export function getSubjectFromToken(token: string): string | null {
  const payload = decodeJWT(token);
  return payload?.sub || null;
}

/** dc.auth may be a raw JWT string or JSON { token, tenant, app_id }. */
export function readDcAuthFromStorage(): {
  token: string | null;
  tenant: string | null;
  appId: string | null;
} {
  if (typeof window === 'undefined') {
    return { token: null, tenant: null, appId: null };
  }

  const raw = localStorage.getItem('dc.auth');
  if (!raw) return { token: null, tenant: null, appId: null };

  const trimmed = raw.trim();
  if (trimmed.startsWith('eyJ') && trimmed.split('.').length === 3) {
    return {
      token: trimmed,
      tenant: getTenantFromToken(trimmed),
      appId: null,
    };
  }

  try {
    const parsed = JSON.parse(trimmed) as {
      token?: string;
      tenant?: string;
      tenantId?: string;
      app_id?: string;
      appId?: string;
    };
    return {
      token: parsed?.token ? String(parsed.token).trim() : null,
      tenant: parsed?.tenant || parsed?.tenantId || null,
      appId: parsed?.app_id || parsed?.appId || null,
    };
  } catch {
    return { token: null, tenant: null, appId: null };
  }
}

export type User = { sub: string; roles: string[] };

function readStoredAuth(): StoredAuth {
  const dc = readDcAuthFromStorage();
  if (dc.token) {
    return { token: dc.token, tenant: dc.tenant || undefined };
  }
  return null;
}

export function getUser(): User | null {
  try {
    const storedAuth = readStoredAuth();
    if (storedAuth?.token) {
      const payload = decodeJWT(storedAuth.token);
      if (payload) {
        const roles: string[] = payload.roles || (payload.role ? [payload.role] : []);
        return { sub: payload.sub || 'unknown', roles };
      }
    }

    if (typeof window === 'undefined') return null;

    const raw = localStorage.getItem('jwt');
    if (!raw) return null;
    const [, payload] = raw.split('.');
    const data = JSON.parse(atob(payload));
    const roles: string[] = data?.roles || (data?.role ? [data.role] : []);
    return { sub: data?.sub || 'unknown', roles };
  } catch {
    return null;
  }
}

export function hasOps(): boolean {
  const roles = getUser()?.roles || [];
  return roles.includes('ops') || roles.includes('admin');
}

export function getJwt(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const storedAuth = readStoredAuth();
    if (storedAuth?.token) return storedAuth.token;

    const stored =
      localStorage.getItem('EDGE_JWT') ||
      localStorage.getItem('auth_token') ||
      localStorage.getItem('jwt');

    if (stored) {
      return stored.startsWith('Bearer ') ? stored.substring(7) : stored;
    }

    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'Authorization' && value?.startsWith('Bearer ')) {
        return value.substring(7);
      }
    }
  } catch (error) {
    console.warn('Failed to resolve JWT token', error);
  }

  return null;
}

export function authHeaders(): Record<string, string> {
  const jwt = getJwt();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (jwt) {
    headers.Authorization = `Bearer ${jwt}`;
  }

  const tenant = getTenant();
  if (tenant) {
    headers['x-tenant-id'] = tenant;
  }

  return headers;
}

export function hasRole(requiredRoles: string[]): boolean {
  const token = getJwt();
  if (!token) return false;

  const payload = decodeJWT(token);
  if (!payload) return false;

  const roles: string[] = payload.roles || (payload.role ? [payload.role] : []);
  return requiredRoles.some((role) => roles.includes(role));
}

export function useRole() {
  const token = getJwt();

  if (!token) {
    return { roles: [], isOps: false, isAdmin: false, canMutate: false };
  }

  const payload = decodeJWT(token);
  if (!payload) {
    return { roles: [], isOps: false, isAdmin: false, canMutate: false };
  }

  const roles: string[] = payload.roles || (payload.role ? [payload.role] : []);
  const isOps = roles.includes('ops');
  const isAdmin = roles.includes('admin');
  const canMutate = isOps || isAdmin;

  return { roles, isOps, isAdmin, canMutate };
}

export const tenantHeaders = tenantHeadersImpl;
export { withTenant };
